import {useState, useEffect} from 'react';
import {ThemeProvider} from '@mui/material';
import theme from '../../theme';
import PaymentListComponent from './PaymentListComponent';
import {usePaymentList} from './hooks';
import {
  PaymentListOrderBy,
  PaymentStatus,
  PAYMENT_LIST_PAGE_SIZE,
} from './constant';
import {useDebounce} from '$modules/frontdesk/hooks';
import {PaymentInfo} from './types';
import {PaymentStatusInput} from '../../utils';
import {getRangeStartDate, getRangeEndDate} from '../../utils';
import LocalStorage from '$utils/localStore';
import useTimezone from '../../../../hooks/useTimezone';
import moment from 'moment';

interface PaymentListContainerProps {
  filteredStatus?: PaymentStatus;
}

function PaymentListContainer(props: PaymentListContainerProps = {}) {
  const {filteredStatus} = props;
  const timezone = useTimezone();

  const [searchText, setSearchText] = useState('');
  const debouncedSearchTerm = useDebounce<string>(searchText, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortActiveColumn, setSortActiveColumn] =
    useState<PaymentListOrderBy>();
  const [status, setStatus] = useState<PaymentStatus | undefined>(() => {
    if (filteredStatus) return filteredStatus;
    const savedStatus = LocalStorage.getItem(
      LocalStorage.PAYMENT_LIST_SELECTED_STATUS
    );
    return savedStatus ? (savedStatus as PaymentStatus) : undefined;
  });
  const [sortDirection, setSortDirection] = useState<boolean>(true);

  const [currentDate, setCurrentDate] = useState<Date | null>(() => {
    const savedDate = LocalStorage.getItem(
      LocalStorage.PAYMENT_LIST_SELECTED_DATE
    );
    return savedDate
      ? new Date(savedDate)
      : moment().tz(timezone).startOf('day').toDate();
  });

  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('day');

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    string[]
  >([]);

  const [appliedPaymentMethods, setAppliedPaymentMethods] = useState<string[]>(
    []
  );

  const [amountRange, setAmountRange] = useState<number[]>([0, 400000]);
  const [appliedAmountRange, setAppliedAmountRange] = useState<number[]>([
    0, 400000,
  ]);
  const [hasAppliedAmountFilter, setHasAppliedAmountFilter] =
    useState<boolean>(false);

  const {
    payments,
    totalPages,
    totalRecords,
    isLoading,
    isError,
    message,
    paid_payment_count,
    unpaid_payment_count,
    all_payment_count,
    min_amount_range,
    max_amount_range,
  } = usePaymentList({
    search: debouncedSearchTerm.trim(),
    pageNumber: currentPage,
    perPage: PAYMENT_LIST_PAGE_SIZE,
    orderBy: sortActiveColumn,
    orderDirection: sortDirection ? 'desc' : 'asc',
    status: status,
    fromDate: currentDate
      ? getRangeStartDate(currentDate, viewMode)
      : undefined,
    toDate: currentDate ? getRangeEndDate(currentDate, viewMode) : undefined,

    ...(hasAppliedAmountFilter
      ? {min_amount: appliedAmountRange[0], max_amount: appliedAmountRange[1]}
      : {}),
    ...(appliedPaymentMethods.length > 0
      ? {
          payment_method: appliedPaymentMethods.map(m => m.toLowerCase()),
        }
      : {}),
  });

  useEffect(() => {
    if (min_amount_range !== undefined && max_amount_range !== undefined) {
      if (!hasAppliedAmountFilter) {
        setAmountRange([min_amount_range, max_amount_range]);
        setAppliedAmountRange([min_amount_range, max_amount_range]);
      }
    }
  }, [min_amount_range, max_amount_range, hasAppliedAmountFilter]);

  useEffect(() => {
    if (currentDate) {
      LocalStorage.setItem(
        LocalStorage.PAYMENT_LIST_SELECTED_DATE,
        currentDate.toISOString()
      );
    }
  }, [currentDate]);

  useEffect(() => {
    if (status !== undefined) {
      LocalStorage.setItem(LocalStorage.PAYMENT_LIST_SELECTED_STATUS, status);
    } else {
      LocalStorage.removeItem(LocalStorage.PAYMENT_LIST_SELECTED_STATUS);
    }
  }, [status]);

  const mappedPayments: PaymentInfo[] = payments.map(payment => ({
    ...payment,
    status: payment.status as PaymentStatusInput,
    patientFullName: payment.patientName,
  }));

  const handleNextPage = () => {
    const updatedPage = Math.min(currentPage + 1, totalPages);
    setCurrentPage(updatedPage);
  };

  const handlePreviousPage = () => {
    const updatedPage = Math.max(currentPage - 1, 1);
    setCurrentPage(updatedPage);
  };

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleSort = (column: PaymentListOrderBy) => {
    if (sortActiveColumn === column) {
      setSortDirection(!sortDirection);
    } else {
      setSortActiveColumn(column);
      setSortDirection(true);
    }
    setCurrentPage(1);
  };

  const handleStatusChange = (newStatus: PaymentStatus | undefined) => {
    setStatus(newStatus);
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setAppliedPaymentMethods([...selectedPaymentMethods]);
    setAppliedAmountRange([...amountRange]);
    setHasAppliedAmountFilter(true);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSelectedPaymentMethods([]);
    setAmountRange([min_amount_range || 0, max_amount_range || 400000]);
    setAppliedPaymentMethods([]);
    setAppliedAmountRange([min_amount_range || 0, max_amount_range || 400000]);
    setHasAppliedAmountFilter(false);
    setCurrentPage(1);
  };

  const handleDateChange = (date: Date | null) => {
    setCurrentDate(date);
    setCurrentPage(1);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [currentDate, viewMode]);

  useEffect(() => {
    if (filteredStatus) {
      setStatus(filteredStatus);
    }
  }, [filteredStatus]);

  return (
    <ThemeProvider theme={theme}>
      <PaymentListComponent
        isLoading={isLoading}
        isError={isError}
        message={message}
        searchText={searchText}
        currentPage={currentPage}
        payments={mappedPayments}
        totalPages={totalPages}
        totalRecords={totalRecords}
        sortActiveColumn={sortActiveColumn}
        sortDirection={sortDirection}
        status={status}
        setSortActiveColumn={setSortActiveColumn}
        setSortDirection={setSortDirection}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onSearchTextChange={handleSearchTextChange}
        setCurrentPage={setCurrentPage}
        onSort={handleSort}
        onStatusChange={handleStatusChange}
        paidCount={paid_payment_count}
        unpaidCount={unpaid_payment_count}
        allPaymentCount={all_payment_count}
        currentDate={currentDate}
        setCurrentDate={handleDateChange}
        viewMode={viewMode}
        setViewMode={setViewMode}
        filterModalOpen={filterModalOpen}
        setFilterModalOpen={setFilterModalOpen}
        selectedPaymentMethods={selectedPaymentMethods}
        setSelectedPaymentMethods={setSelectedPaymentMethods}
        amountRange={amountRange}
        setAmountRange={setAmountRange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        minAmount={min_amount_range}
        maxAmount={max_amount_range}
      />
    </ThemeProvider>
  );
}

export default PaymentListContainer;
